// ---------------------------------------------------------------------------
// phoneFormatting.ts — Live US phone formatting utilities
// ---------------------------------------------------------------------------

const DIGITS_ONLY = /\D/g;

/**
 * Formats a raw phone string into (XXX) XXX-XXXX or (XXX) XXX-XXXX x1234.
 * Handles 10-digit US numbers with optional extension.
 */
export function formatPhone(raw: string): string {
  const digits = raw.replace(DIGITS_ONLY, '');

  // Detect extension: digits after 10
  const main = digits.slice(0, 10);
  const ext = digits.slice(10);

  if (main.length === 0) return '';

  let formatted = '';
  if (main.length <= 3) {
    formatted = `(${main}`;
  } else if (main.length <= 6) {
    formatted = `(${main.slice(0, 3)}) ${main.slice(3)}`;
  } else {
    formatted = `(${main.slice(0, 3)}) ${main.slice(3, 6)}-${main.slice(6)}`;
  }

  if (ext.length > 0) {
    formatted += ` x${ext}`;
  }

  return formatted;
}

/**
 * Returns the count of digit characters in a phone string (excluding extension).
 */
export function phoneDigitCount(raw: string): number {
  return raw.replace(DIGITS_ONLY, '').slice(0, 10).length;
}

/**
 * Returns true if the phone string contains exactly 10 US digits.
 */
export function validatePhone(raw: string): boolean {
  return phoneDigitCount(raw) === 10;
}

/**
 * Extracts the extension from a formatted phone string (digits after position 10).
 * Returns empty string if no extension.
 */
export function extractExtension(raw: string): string {
  const digits = raw.replace(DIGITS_ONLY, '');
  return digits.slice(10);
}

/**
 * Strips formatting and returns only the 10-digit number (no extension).
 */
export function toE164Base(raw: string): string {
  return raw.replace(DIGITS_ONLY, '').slice(0, 10);
}
